import React, { useMemo, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { message } from "antd";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const auditorsColumns = [
  { header: "Code", key: "code", width: 25 },
  { header: "Certificate Number", key: "certificate_number", width: 25 },
  { header: "Institution", key: "institution", width: 25 },
  { header: "Investment Type", key: "investment_type", width: 25 },
  { header: "Regulator", key: "regulator", width: 20 },
  { header: "Rate", key: "rate", width: 15 },
  { header: "Purchase Date", key: "purchase_date", width: 18 },
  { header: "Maturity Date", key: "maturity_date", width: 18 },
  { header: "Tenure", key: "tenure", width: 15 },
  { header: "Days Earned", key: "days_earned", width: 15 },
  { header: "Days Unearned", key: "days_unearned", width: 18 },
  { header: "Cost", key: "cost", width: 18 },

  // NEW

  { header: "Cumulative Interest", key: "cumulative_interest", width: 22 },
  { header: "Interest Unearned", key: "interest_unearned", width: 22 },
  { header: "Impairment", key: "impairment", width: 18 },
  { header: "Fair Value", key: "fair_value", width: 18 },
  { header: "Total Interest", key: "total_interest", width: 18 },
  { header: "Interest Charged", key: "interest_charged", width: 22 },
  { header: "Daily Interest", key: "daily_interest", width: 18 },
  { header: "Rate Type", key: "rate_type", width: 18 },
  { header: "Status", key: "status", width: 25 },
];

const groupedColumns = [
  { header: "Group", key: "group", width: 35 },
  { header: "Number of Investments", key: "count", width: 22 },
  { header: "Cost", key: "cost", width: 18 },

  // NEW

  { header: "Cumulative Interest", key: "cumulative_interest", width: 22 },
  { header: "Interest Unearned", key: "interest_unearned", width: 22 },
  { header: "Impairment", key: "impairment", width: 18 },
  { header: "Fair Value", key: "fair_value", width: 18 },
  { header: "Total Interest", key: "total_interest", width: 18 },
  { header: "Interest Charged", key: "interest_charged", width: 22 },
];

const GROUP_BY_OPTIONS = [
  {
    label: "All / Detailed",
    value: "all",
  },
  {
    label: "Group By Tenure",
    value: "tenure",
  },
  {
    label: "Group By Regulator",
    value: "regulator",
  },
  {
    label: "Group By Institution",
    value: "institution",
  },
  {
    label: "Group By Investment Type",
    value: "investment_type",
  },
];

function money(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function getMonthsWithinRange(startDate, endDate) {
  const start = moment(startDate).startOf("month");
  const end = moment(endDate).startOf("month");

  const months = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, "month")) {
    const monthStart = cursor.clone().startOf("month");
    const monthEnd = cursor.clone().endOf("month");

    months.push({
      sheetName: cursor.format("MMM YYYY"),
      start: moment.max(monthStart, moment(startDate)).format("YYYY-MM-DD"),
      end: moment.min(monthEnd, moment(endDate)).format("YYYY-MM-DD"),
    });

    cursor.add(1, "month");
  }

  return months;
}

function cleanSheetName(name) {
  return String(name || "Sheet")
    .replace(/[\\/*?:[\]]/g, "")
    .substring(0, 31);
}

function getGroupLabel(groupBy) {
  if (groupBy === "tenure") return "Tenure";
  if (groupBy === "regulator") return "Regulator";
  if (groupBy === "institution") return "Institution";
  if (groupBy === "investment_type") return "Investment Type";
  return "All / Detailed";
}

function getGroupValue(row, groupBy) {
  if (groupBy === "tenure") return row.tenure || "Unknown Tenure";
  if (groupBy === "regulator") return row.regulator || "Unknown Regulator";
  if (groupBy === "institution")
    return row.institution || "Unknown Institution";
  if (groupBy === "investment_type") {
    return row.investment_type || "Unknown Investment Type";
  }

  return "All";
}

function groupRows(rows, groupBy) {
  const grouped = {};

  rows.forEach((row) => {
    const group = getGroupValue(row, groupBy);

    if (!grouped[group]) {
      grouped[group] = {
        group,
        count: 0,
        cost: 0,
        cumulative_interest: 0,
        interest_unearned: 0,
        impairment: 0,
        fair_value: 0,
        total_interest: 0,
        interest_charged: 0,
      };
    }

    grouped[group].count += 1;
    grouped[group].cost += money(row.cost);
    grouped[group].cumulative_interest += money(row.cumulative_interest);
    grouped[group].interest_unearned += money(row.interest_unearned);
    grouped[group].impairment += money(row.impairment);
    grouped[group].fair_value += money(row.fair_value);
    grouped[group].total_interest += money(row.total_interest);
    grouped[group].interest_charged += money(row.interest_charged);
  });

  return Object.values(grouped).sort((a, b) =>
    String(a.group).localeCompare(String(b.group)),
  );
}

function calculateInvestmentRow(investment, interestStart, interestEnd) {
  let cost = money(investment.cost);
  const impairment = money(investment.impaired_amount);
  const total_interest = money(investment.total_interest);
  const daily_interest = money(investment.daily_interest);

  const purchase_date = moment(investment.purchase_date).format("YYYY-MM-DD");
  const maturity_date = moment(investment.maturity_date).format("YYYY-MM-DD");

  const tenure = Number(investment.tenure || 0);
  const rate_type = investment.rate_type;

  let status = moment(maturity_date).isAfter(moment()) ? "Active" : "Matured";

  let days_earned = moment().diff(moment(purchase_date), "days") + 1;
  let interest_charged = days_earned * daily_interest;
  let days_unearned = tenure - days_earned;
  let cumulative_interest = days_earned * daily_interest;
  let interest_unearned = days_unearned * daily_interest;

  let fair_value =
    rate_type === "interest rate"
      ? cost + cumulative_interest - impairment
      : cost - impairment;

  if (status === "Matured") {
    days_earned = moment(maturity_date).diff(moment(purchase_date), "days");
    days_unearned = 0;
    cumulative_interest = days_earned * daily_interest;
    interest_unearned = 0;
    interest_charged = 0;

    fair_value =
      rate_type === "interest rate"
        ? cost + cumulative_interest - impairment
        : cost - impairment;
  }

  if (interestStart && interestEnd) {
    const start = moment(interestStart);
    const end = moment(interestEnd);
    const purchase = moment(purchase_date);
    const maturity = moment(maturity_date);

    if (
      purchase.isBefore(start) &&
      maturity.isBefore(start) &&
      status === "Matured"
    ) {
      cumulative_interest = cost;
      interest_unearned = 0;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (end.isBefore(purchase)) {
      cost = 0;
      fair_value = 0;
      interest_unearned = 0;
      cumulative_interest = 0;
      days_earned = 0;
      days_unearned = 0;
      interest_charged = 0;
      status = "investment was not available then";
    }

    if (purchase.isSameOrBefore(start) && maturity.isAfter(end)) {
      const diff = end.diff(start, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (
      purchase.isBefore(start) &&
      maturity.isSameOrAfter(start) &&
      maturity.isSameOrBefore(end)
    ) {
      const diff = maturity.diff(start, "days");
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = tenure - cummDateDiff;
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = cummDateDiff * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = diff * daily_interest;
    }

    if (maturity.isBefore(start)) {
      interest_unearned = 0;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (
      purchase.isAfter(start) &&
      purchase.isSameOrBefore(end) &&
      maturity.isAfter(end)
    ) {
      const diff = end.diff(purchase, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (purchase.isSameOrAfter(start) && maturity.isSameOrBefore(end)) {
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = 0;
      interest_unearned = 0;
      cumulative_interest = tenure * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      const diff = maturity.diff(purchase, "days");
      interest_charged = diff * daily_interest;
    }
  }

  return {
    code: investment.code,
    certificate_number: investment.certificate_number,
    institution: investment.institution,
    investment_type: investment.investment_type,
    regulator: investment.regulator,
    rate: investment.rate,
    purchase_date,
    maturity_date,
    tenure,
    days_earned,
    days_unearned,
    cost,
    cumulative_interest,
    interest_unearned,
    impairment,
    fair_value,
    total_interest,
    interest_charged,
    daily_interest,
    rate_type,
    status,
  };
}

function getSummary(rows) {
  return {
    totalCost: rows.reduce((sum, row) => sum + money(row.cost), 0),

    totalCumulativeInterest: rows.reduce(
      (sum, row) => sum + money(row.cumulative_interest),
      0,
    ),
    totalInterestUnearned: rows.reduce(
      (sum, row) => sum + money(row.interest_unearned),
      0,
    ),
    totalInterestCharged: rows.reduce(
      (sum, row) => sum + money(row.interest_charged),
      0,
    ),
    totalFairValue: rows.reduce((sum, row) => sum + money(row.fair_value), 0),
  };
}

function styleHeaderRow(row) {
  row.font = { bold: true };

  row.eachCell((cell) => {
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };

    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9EAF7" },
    };
  });
}
function styleSummarySection(worksheet, startRow, endRow, columnCount = 4) {
  for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    for (let col = 1; col <= columnCount; col++) {
      const cell = row.getCell(col);

      cell.border = {
        top: { style: "thin", color: { argb: "FFCBD5E1" } },
        left: { style: "thin", color: { argb: "FFCBD5E1" } },
        bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
        right: { style: "thin", color: { argb: "FFCBD5E1" } },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: col === 1 || col === 3 ? "left" : "right",
      };
    }
  }

  const titleRow = worksheet.getRow(startRow);

  for (let col = 1; col <= columnCount; col++) {
    const cell = titleRow.getCell(col);

    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
      size: 12,
    };

    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF355C80" },
    };

    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };
  }

  for (let rowNumber = startRow + 1; rowNumber <= endRow; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    row.getCell(1).font = {
      bold: true,
      color: { argb: "FF334155" },
    };

    row.getCell(3).font = {
      bold: true,
      color: { argb: "FF334155" },
    };

    row.getCell(2).font = {
      bold: true,
      color: { argb: "FF0F172A" },
    };

    row.getCell(4).font = {
      bold: true,
      color: { argb: "FF0F172A" },
    };

    row.getCell(2).numFmt = "#,##0.00";
    row.getCell(4).numFmt = "#,##0.00";

    for (let col = 1; col <= columnCount; col++) {
      row.getCell(col).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: rowNumber % 2 === 0 ? "FFF8FAFC" : "FFFFFFFF" },
      };
    }
  }

  worksheet.getColumn(1).width = Math.max(
    worksheet.getColumn(1).width || 0,
    38,
  );
  worksheet.getColumn(2).width = Math.max(
    worksheet.getColumn(2).width || 0,
    18,
  );
  worksheet.getColumn(3).width = Math.max(
    worksheet.getColumn(3).width || 0,
    38,
  );
  worksheet.getColumn(4).width = Math.max(
    worksheet.getColumn(4).width || 0,
    18,
  );
}
function styleBodyRows(worksheet, startRow, endRow) {
  for (let rowNumber = startRow; rowNumber <= endRow; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "left",
      };
    });
  }
}

function applyNumberFormat(worksheet, columns) {
  columns.forEach((column, index) => {
    if (
      [
        "cost",
        "cumulative_interest",
        "interest_unearned",
        "impairment",
        "fair_value",
        "total_interest",
        "interest_charged",
        "daily_interest",
      ].includes(column.key)
    ) {
      worksheet.getColumn(index + 1).numFmt = "#,##0.00";
    }
  });
}

function setupWorksheetColumns(worksheet, columns) {
  worksheet.columns = columns.map((column) => ({
    key: column.key,
    width: column.width,
  }));
}

function addReportTitle(worksheet, title, subtitle, columnCount) {
  worksheet.addRow([title]);
  worksheet.addRow([subtitle]);
  worksheet.addRow([]);

  worksheet.mergeCells(1, 1, 1, columnCount);
  worksheet.mergeCells(2, 1, 2, columnCount);

  worksheet.getCell("A1").font = {
    bold: true,
    size: 14,
  };

  worksheet.getCell("A2").font = {
    bold: true,
    size: 11,
  };
}

function addTableHeader(worksheet, columns) {
  const headerRow = worksheet.addRow(columns.map((column) => column.header));
  styleHeaderRow(headerRow);
  return headerRow.number;
}

function addTableRows(worksheet, rows, columns) {
  const startRow = worksheet.rowCount + 1;

  rows.forEach((row) => {
    worksheet.addRow(columns.map((column) => row[column.key]));
  });

  const endRow = worksheet.rowCount;

  if (rows.length > 0) {
    styleBodyRows(worksheet, startRow, endRow);
  }
}

function addSummaryRows(
  worksheet,
  rows,
  cumulativeInterestChargedFromPeriodStart,
) {
  const summary = getSummary(rows);

  const summaryStartRow = worksheet.rowCount + 1;

  worksheet.addRow(["SUMMARY", "", "", ""]);
  worksheet.mergeCells(summaryStartRow, 1, summaryStartRow, 4);

  worksheet.addRow(["Cost", summary.totalCost]);

  worksheet.addRow([
    "Cumulative Interest",
    summary.totalCumulativeInterest,
    "Interest Unearned",
    summary.totalInterestUnearned,
  ]);

  worksheet.addRow([
    "Interest Charged",
    summary.totalInterestCharged,
    "Cummulative Interest Charged From Period Start",
    cumulativeInterestChargedFromPeriodStart,
  ]);

  worksheet.addRow(["Fair Value", summary.totalFairValue, "", ""]);

  const summaryEndRow = worksheet.rowCount;

  styleSummarySection(worksheet, summaryStartRow, summaryEndRow, 4);

  worksheet.addRow([]);
}

function addMonthlyWorksheet({
  workbook,
  month,
  detailedRows,
  groupBy,
  cumulativeInterestChargedFromPeriodStart,
}) {
  const worksheet = workbook.addWorksheet(cleanSheetName(month.sheetName));

  const isDetailed = groupBy === "all";
  const columns = isDetailed ? auditorsColumns : groupedColumns;
  const rowsToExport = isDetailed
    ? detailedRows
    : groupRows(detailedRows, groupBy);

  setupWorksheetColumns(worksheet, columns);

  addReportTitle(
    worksheet,
    `Auditors Investment Report - ${month.sheetName}`,
    `Period: ${month.start} to ${month.end} | View: ${getGroupLabel(groupBy)}`,
    columns.length,
  );

  addSummaryRows(
    worksheet,
    detailedRows,
    cumulativeInterestChargedFromPeriodStart,
  );

  const tableHeaderRowNumber = addTableHeader(worksheet, columns);

  addTableRows(worksheet, rowsToExport, columns);

  worksheet.views = [
    {
      state: "frozen",
      ySplit: tableHeaderRowNumber,
      topLeftCell: `A${tableHeaderRowNumber + 1}`,
    },
  ];

  applyNumberFormat(worksheet, columns);

  return worksheet;
}

function addActiveInvestmentsWorksheet({ workbook, activeRows, periodEnd }) {
  const worksheet = workbook.addWorksheet(
    cleanSheetName("Active Investment at Period End"),
  );

  setupWorksheetColumns(worksheet, auditorsColumns);

  addReportTitle(
    worksheet,
    "Active Investment as at Period End",
    `Investments with maturity date greater than ${periodEnd} and purchase date less than or equal to ${periodEnd}`,
    auditorsColumns.length,
  );

  const summary = getSummary(activeRows);

  const summaryStartRow = worksheet.rowCount + 1;

  worksheet.addRow(["SUMMARY", "", "", ""]);
  worksheet.mergeCells(summaryStartRow, 1, summaryStartRow, 4);

  worksheet.addRow([
    "Number of Active Investments",
    activeRows.length,
    "Cost",
    summary.totalCost,
  ]);

  worksheet.addRow(["Cumulative Interest", summary.totalCumulativeInterest]);

  worksheet.addRow([
    "Interest Unearned",
    summary.totalInterestUnearned,
    "Interest Charged",
    summary.totalInterestCharged,
  ]);

  worksheet.addRow(["Fair Value", summary.totalFairValue, "", ""]);

  const summaryEndRow = worksheet.rowCount;

  styleSummarySection(worksheet, summaryStartRow, summaryEndRow, 4);

  worksheet.addRow([]);

  const tableHeaderRowNumber = addTableHeader(worksheet, auditorsColumns);

  addTableRows(worksheet, activeRows, auditorsColumns);

  worksheet.views = [
    {
      state: "frozen",
      ySplit: tableHeaderRowNumber,
      topLeftCell: `A${tableHeaderRowNumber + 1}`,
    },
  ];

  applyNumberFormat(worksheet, auditorsColumns);

  return worksheet;
}

export default function AuditorsInvestmentReport() {
  const approvedInvestments = useSelector((state) => state.approvedInvestment);

  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const [groupBy, setGroupBy] = useState("all");

  const months = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return [];
    return getMonthsWithinRange(dateRange.startDate, dateRange.endDate);
  }, [dateRange.startDate, dateRange.endDate]);

  const exportAuditorsReport = async () => {
    let loadingId;

    try {
      if (!dateRange.startDate || !dateRange.endDate) {
        toast.error("Please select start date and end date");
        return;
      }

      if (moment(dateRange.endDate).isBefore(moment(dateRange.startDate))) {
        toast.error("End date cannot be before start date");
        return;
      }

      if (
        !Array.isArray(approvedInvestments) ||
        approvedInvestments.length === 0
      ) {
        toast.error("No approved investments found");
        return;
      }

      loadingId = toast.loading("Generating auditors report...");

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Investment System";
      workbook.created = new Date();

      let cumulativeInterestChargedFromPeriodStart = 0;

      months.forEach((month) => {
        const detailedRows = approvedInvestments
          .filter((investment) => {
            const purchaseDate = moment(investment.purchase_date);
            return purchaseDate.isSameOrBefore(moment(month.end));
          })
          .map((investment) =>
            calculateInvestmentRow(investment, month.start, month.end),
          )
          .filter(
            (row) => Math.abs(Number(row.interest_charged || 0)) > 0.0001,
          );

        const totalInterestChargedForThisMonth = detailedRows.reduce(
          (sum, row) => sum + money(row.interest_charged),
          0,
        );

        cumulativeInterestChargedFromPeriodStart +=
          totalInterestChargedForThisMonth;

        addMonthlyWorksheet({
          workbook,
          month,
          detailedRows,
          groupBy,
          cumulativeInterestChargedFromPeriodStart,
        });
      });

      const activeRows = approvedInvestments
        .filter((investment) => {
          const maturityDate = moment(investment.maturity_date);
          const purchaseDate = moment(investment.purchase_date);
          const reportEndDate = moment(dateRange.endDate);

          return (
            maturityDate.isAfter(reportEndDate) &&
            purchaseDate.isSameOrBefore(reportEndDate)
          );
        })
        .map((investment) =>
          calculateInvestmentRow(
            investment,
            dateRange.startDate,
            dateRange.endDate,
          ),
        );

      addActiveInvestmentsWorksheet({
        workbook,
        activeRows,
        periodEnd: dateRange.endDate,
      });

      const buffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `auditors-investment-report-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`,
      );

      toast.remove(loadingId);
      message.success("Auditors report generated successfully");
    } catch (error) {
      console.error(error);

      if (loadingId) {
        toast.remove(loadingId);
      }

      toast.error(error.message || "Failed to generate auditors report");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: "22px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          overflow: "hidden",
          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: { xs: 2, md: 3 },
            py: { xs: 2.2, md: 2.7 },
            background:
              "linear-gradient(135deg, #355c80 0%, #2d4e6c 55%, #243f57 100%)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -70,
              top: -90,
              width: 210,
              height: 210,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.10)",
              filter: "blur(22px)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: "42%",
              bottom: -100,
              width: 200,
              height: 200,
              borderRadius: "50%",
              backgroundColor: "rgba(23,184,89,0.14)",
              filter: "blur(24px)",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.3,
                  py: 0.4,
                  mb: 1,
                  borderRadius: "999px",
                  border: "1px solid rgba(255,255,255,0.22)",
                  backgroundColor: "rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.92)",
                  fontSize: 10.5,
                  fontWeight: 800,
                }}
              >
                Investment Audit Export
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: "#fff",
                  letterSpacing: "-0.02em",
                  fontSize: { xs: 17, md: 19 },
                }}
              >
                Auditors Investment Report
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.76)",
                  maxWidth: 760,
                  mt: 0.7,
                  lineHeight: 1.6,
                  fontSize: 12.5,
                }}
              >
                Select a reporting period and grouping option. The system will
                generate one Excel workbook with monthly sheets and an
                additional active investment sheet as at the selected end date.
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 175,
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.16)",
                backgroundColor: "rgba(255,255,255,0.10)",
                px: 1.7,
                py: 1.2,
                color: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.70)",
                }}
              >
                Current Grouping
              </Typography>

              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: 12.5,
                  fontWeight: 900,
                }}
              >
                {getGroupLabel(groupBy)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 2, md: 2.7 } }}>
          {/* Filters */}
          <Box
            sx={{
              borderRadius: "18px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              p: { xs: 1.8, md: 2.2 },
            }}
          >
            <Box sx={{ mb: 1.7 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 900,
                  color: "#1e293b",
                }}
              >
                Report Filters
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: 12.5,
                  color: "#64748b",
                }}
              >
                Choose the start date, end date, and how the workbook should be
                grouped.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  lg: "1fr 1fr 1.1fr auto",
                },
                gap: 1.6,
                alignItems: "end",
              }}
            >
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    mb: 0.6,
                    fontSize: 10,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#64748b",
                  }}
                >
                  Start Date
                </Typography>

                <Box
                  component="input"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  sx={{
                    width: "100%",
                    height: 39,
                    borderRadius: "13px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                    px: 1.3,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "#334155",
                    outline: "none",
                    boxSizing: "border-box",
                    "&:focus": {
                      borderColor: "#355c80",
                      boxShadow: "0 0 0 4px rgba(53,92,128,0.10)",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    mb: 0.6,
                    fontSize: 10,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#64748b",
                  }}
                >
                  End Date
                </Typography>

                <Box
                  component="input"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  sx={{
                    width: "100%",
                    height: 39,
                    borderRadius: "13px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                    px: 1.3,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "#334155",
                    outline: "none",
                    boxSizing: "border-box",
                    "&:focus": {
                      borderColor: "#355c80",
                      boxShadow: "0 0 0 4px rgba(53,92,128,0.10)",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: "block",
                    mb: 0.6,
                    fontSize: 10,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#64748b",
                  }}
                >
                  Group By
                </Typography>

                <TextField
                  select
                  size="small"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 39,
                      borderRadius: "13px",
                      backgroundColor: "#fff",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: "#334155",
                    },
                    "& .MuiSelect-select": {
                      py: 0.8,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#355c80",
                      },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#355c80",
                        borderWidth: 1,
                      },
                  }}
                >
                  {GROUP_BY_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ fontSize: 12.5 }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Button
                variant="contained"
                onClick={exportAuditorsReport}
                sx={{
                  height: 39,
                  px: 2.4,
                  borderRadius: "13px",
                  backgroundColor: "#355c80",
                  textTransform: "capitalize",
                  fontSize: 12.5,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  boxShadow: "0 10px 20px rgba(53,92,128,0.22)",
                  "&:hover": {
                    backgroundColor: "#2d4e6c",
                    boxShadow: "0 12px 24px rgba(53,92,128,0.28)",
                  },
                }}
              >
                Export Report
              </Button>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, 1fr)",
              },
              gap: 1.6,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                p: 2,
                boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#94a3b8",
                }}
              >
                Monthly Sheets
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: 27,
                  lineHeight: 1,
                  fontWeight: 950,
                  color: "#355c80",
                }}
              >
                {months.length}
              </Typography>

              <Typography sx={{ mt: 0.8, fontSize: 12, color: "#64748b" }}>
                {months.length > 0
                  ? "Based on selected date range"
                  : "Select a period to preview sheets"}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                p: 2,
                boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#94a3b8",
                }}
              >
                Extra Sheet
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: 14.5,
                  fontWeight: 950,
                  color: "#355c80",
                }}
              >
                Active Investment
              </Typography>

              <Typography sx={{ mt: 0.8, fontSize: 12, color: "#64748b" }}>
                Investments running as at the selected period end date.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: "1px solid rgba(53,92,128,0.18)",
                background:
                  "linear-gradient(135deg, rgba(53,92,128,0.08) 0%, #ffffff 80%)",
                p: 2,
                boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#355c80",
                }}
              >
                Grouping
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: 14.5,
                  fontWeight: 950,
                  color: "#1e293b",
                }}
              >
                {getGroupLabel(groupBy)}
              </Typography>

              <Typography sx={{ mt: 0.8, fontSize: 12, color: "#64748b" }}>
                Workbook rows will follow this grouping option.
              </Typography>
            </Paper>
          </Box>

          {/* Workbook Preview */}
          {months.length > 0 ? (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                p: 2,
                boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
              }}
            >
              <Box
                sx={{
                  mb: 1.7,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 1.2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 900,
                      color: "#1e293b",
                    }}
                  >
                    Workbook Preview
                  </Typography>

                  <Typography sx={{ mt: 0.3, fontSize: 12, color: "#64748b" }}>
                    The following sheets will be created in the Excel workbook.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    borderRadius: "999px",
                    backgroundColor: "rgba(53,92,128,0.10)",
                    color: "#355c80",
                    px: 1.3,
                    py: 0.6,
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                >
                  {months.length + 1} total sheets
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.8,
                }}
              >
                {months.map((month) => (
                  <Box
                    key={month.sheetName}
                    sx={{
                      borderRadius: "999px",
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#f8fafc",
                      px: 1.3,
                      py: 0.65,
                      fontSize: 11.2,
                      fontWeight: 800,
                      color: "#475569",
                    }}
                  >
                    {month.sheetName}
                  </Box>
                ))}

                <Box
                  sx={{
                    borderRadius: "999px",
                    border: "1px solid rgba(53,92,128,0.20)",
                    backgroundColor: "rgba(53,92,128,0.08)",
                    px: 1.3,
                    py: 0.65,
                    fontSize: 11.2,
                    fontWeight: 900,
                    color: "#355c80",
                  }}
                >
                  Active Investment as at Period End
                </Box>
              </Box>
            </Paper>
          ) : (
            <Box
              sx={{
                mt: 2,
                borderRadius: "18px",
                border: "1px dashed #cbd5e1",
                backgroundColor: "#f8fafc",
                p: 2.5,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: "#334155",
                }}
              >
                No workbook preview yet
              </Typography>

              <Typography sx={{ mt: 0.4, fontSize: 12, color: "#64748b" }}>
                Select a valid start date and end date to preview the monthly
                sheets before exporting.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
